/**
 * Api Error Class
 *
 * Why: Specialized handling for API response errors
 * What: Error class for HTTP responses with error status codes
 * BENEFIT:
 *      - Automatic parsing of API error responses
 *      - Integration with form validation
 *      - Status-based categorization
 *
 * Use cases:
 *      -4xx client errors
 *      -5xx server errors
 *      - Any HTTP error response from API
 */

import { AppError, type AppErrorOptions } from "./base/AppError";

import {
  ErrorCategory,
  type ErrorContext,
  type ValidationFieldError,
} from "../types";
import { type ApiErrorResponse } from "../types";
import { getHttpStatusMeta } from "../constants";
import { type ErrorCode } from "../constants";
import { AxiosError } from "axios"; // Add this if using Axios

// Define your server error response structure
interface ServerErrorResponse {
  success: false;
  error: {
    message: string;
    errorCode: string;
    statusCode: number;
    timestamp: string;
    name: string;
    path: string;
    method: string;
    // Optional fields that might appear
    details?: Record<string, unknown>;
    stack?: string; // in development
  };
}

// Axios error wrapper (if using Axios)
type AxiosServerError = AxiosError<ServerErrorResponse>;

/**
 * Options specific to API Errors
 */
export interface ApiErrorOptions extends Omit<AppErrorOptions, "statusCode"> {
  // Request ID from server (for tracing)
  requestId?: string;
  /** API path that failed */
  path?: string;
  /** Raw error response from API */
  errors?: Record<string, string[]>;
}

/**
 * API Error
 *
 * Represents errors returned from API endpoints.
 * Automatically determines category and severity from status code
 */

export class ApiError extends AppError {
  /**
   * Request Id from server response
   * Used for correleting with server logs
   */
  public readonly requestId?: string;
  /**
   * Api path that failed
   * Useful for debugging
   */
  public readonly path?: string;
  /**
   * Raw field errors from API
   * Original format:{fieldName:['error1','error2']}
   */
  public readonly errors?: Record<string, string[]>;

  constructor(
    message: string,
    statusCode: number,
    options: ApiErrorOptions = {},
  ) {
    // Get metadata for this status code
    const statusMeta = getHttpStatusMeta(statusCode);

    // Call parent constructor with appropriate defaults
    super(message, {
      statusCode,
      code: options.code ?? statusMeta.code,
      category: options.category ?? statusMeta.category,
      severity: options.severity ?? statusMeta.severity,
      isOperational: options.isOperational ?? true,
      isRetryable: options.isRetryable ?? statusMeta.isRetryable,
      context: options.context,
      validationErrors:
        options.validationErrors ??
        ApiError.parseValidationErrors(options.errors),
      cause: options.cause,
    });

    this.requestId = options.requestId;
    this.path = options.path;
    this.errors = options.errors;
  }

  /**
   * Create ApiError fromAPI error response
   *
   * Factory method for creating errors from API responses
   * Handles parsing of various response formats
   */
  static fromResponse(response: ApiErrorResponse, cause?: unknown): ApiError {
    console.log("ApiErrorRessponse", response);
    return new ApiError(response.message, response.statusCode, {
      code: response.code as ErrorCode | undefined,
      errors: response.errors,
      requestId: response.requestId,
      path: response.path,
      cause,
    });
  }

  /**
   * Create ApiError from HTTP status code
   * Useful when response body is empty or unparsable
   */
  static fromStatus(
    statusCode: number,
    error?: unknown,
    context?: Partial<ErrorContext>,
  ): ApiError {
    const meta = getHttpStatusMeta(statusCode);

    // Type guard for server error response
    const isServerErrorResponse = (
      err: unknown,
    ): err is ServerErrorResponse => {
      if (!err || typeof err !== "object") return false;

      const possibleResponse = err as Partial<ServerErrorResponse>;
      return (
        possibleResponse.success === false &&
        possibleResponse.error !== null &&
        typeof possibleResponse.error === "object"
      );
    };

    // Type guard for Axios error
    const isAxiosError = (err: unknown): err is AxiosServerError => {
      return (
        typeof err === "object" &&
        err !== null &&
        "isAxiosError" in err &&
        (err as any).isAxiosError === true
      );
    };

    // Type guard for object with response property
    const hasResponseWithData = (
      err: unknown,
    ): err is { response: { data: unknown } } => {
      return (
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        err.response !== null &&
        typeof err.response === "object" &&
        "data" in err.response
      );
    };

    // Type guard for object with message property
    const hasMessage = (err: unknown): err is { message: string } => {
      return (
        typeof err === "object" &&
        err !== null &&
        "message" in err &&
        typeof (err as { message: unknown }).message === "string"
      );
    };

    // Extract server message from error
    let serverMessage: string | undefined;
    let errorPath: string | undefined;
    let errorDetails: Record<string, unknown> | undefined;

    // Handle different error structures
    if (error && typeof error === "object") {
      // Case 1: Direct server error response (as shown in your example)
      if (isServerErrorResponse(error)) {
        serverMessage = error.error.message;
        errorPath = error.error.path;
        errorDetails = error.error.details;
      }
      // Case 2: Axios error with response
      else if (isAxiosError(error)) {
        const responseData = error.response?.data;

        if (isServerErrorResponse(responseData)) {
          serverMessage = responseData.error.message;
          errorPath = responseData.error.path;
          errorDetails = responseData.error.details;
        }
      }
      // Case 3: Error object with response property
      else if (hasResponseWithData(error)) {
        const responseData = error.response.data;

        if (isServerErrorResponse(responseData)) {
          serverMessage = responseData.error.message;
          errorPath = responseData.error.path;
          errorDetails = responseData.error.details;
        }
      }
      // Case 4: Standard Error object
      else if (error instanceof Error) {
        serverMessage = error.message;
      }
      // Case 5: Raw message property
      else if (hasMessage(error)) {
        serverMessage = error.message;
      }
    }

    // Create error message with proper fallback priority:
    // 1. Server error message from response
    // 2. Meta message from status code
    // 3. Generic HTTP error
    const errorMessage =
      serverMessage || meta.message || `HTTP Error ${statusCode}`;

    // Build proper ErrorContext object (not Partial)
    const enhancedContext: ErrorContext = {
      timestamp: new Date(), // Always provide a timestamp
      ...(context || {}), // Spread the partial context
    };

    // Add path if available from server response
    if (errorPath) {
      enhancedContext.path = errorPath;
    }

    // Add any additional details
    if (errorDetails) {
      enhancedContext.details = errorDetails;
    }

    // Add error code from server if available
    const code = meta.code;

    return new ApiError(errorMessage, statusCode, {
      code,
      cause: error, // Pass the original error as cause
      context: enhancedContext,
      // If the server response had a path, use it
      path: errorPath,
    });
  }
  /**
   * Parse validation errors from API response
   * Converts API format { field: ['msg1', 'msg2'] }
   * to internal format [{ field, message }]
   */
  private static parseValidationErrors(
    errors?: Record<string, string[]>,
  ): ValidationFieldError[] | undefined {
    if (!errors) return undefined;

    const validationErrors: ValidationFieldError[] = [];
    for (const [field, messages] of Object.entries(errors)) {
      for (const message of messages) {
        validationErrors.push({ field, message });
      }
    }
    return validationErrors.length > 0 ? validationErrors : undefined;
  }

  /**
   * Get field errors as a flat object
   *
   * Returns format compatible with react-hook-form
   * {fieldName:'first error message'}
   */
  public getFieldErrors(): Record<string, string> {
    const fieldErrors: Record<string, string> = {}; // ✅ Object, not array

    // From parsed validation errors
    if (this.validationErrors) {
      for (const { field, message } of this.validationErrors) {
        if (!fieldErrors[field]) {
          fieldErrors[field] = message;
        }
      }
    }
    // From raw api errors fallback
    if (this.errors) {
      for (const [field, messages] of Object.entries(this.errors)) {
        if (!fieldErrors[field] && messages.length > 0) {
          fieldErrors[field] = messages[0];
        }
      }
    }
    return fieldErrors;
  }

  /**
   * Get all error messages for a specific field
   */
  public getFieldErrorMessages(fieldId: string): string[] {
    const messages: string[] = [];

    if (this.validationErrors) {
      messages.push(
        ...this.validationErrors
          .filter((e) => e.field === fieldId)
          .map((e) => e.message),
      );
    }
    return messages;
  }

  /**
   * Check if this is a validation error (422)
   */
  public isValidationError(): boolean {
    return this.category === ErrorCategory.VALIDATION;
  }

  /**
   * Check if this is an authentication error (401)
   */
  public isAuthenticationError(): boolean {
    return this.category === ErrorCategory.AUTHENTICATION;
  }
  /**
   * Check if this is an authorization error (403)
   */
  public isAuthorizationError(): boolean {
    return this.category === ErrorCategory.AUTHORIZATION;
  }

  /**
   * Check if this is not found error (404)
   */
  public isNotFoundError(): boolean {
    return this.category === ErrorCategory.NOT_FOUND;
  }
  /**
   * Check if this is a server error (5xx)
   */
  public isServerError(): boolean {
    return this.category === ErrorCategory.SERVER;
  }
}
